import { SowInput, SowTextarea, SowSelect } from './ValidationFields';
import type { CSSProperties } from 'react';
import { Button } from '../Button';
import { PhaseHoursEditor } from './PhaseHoursEditor';
import {
  EXEC_COACHING_TIER_OPTIONS,
  applyExecCoachingScope,
  applyExecCoachingTier,
  selectedExecCoachingTier,
  updateExecCoachingIdentity,
} from '../../lib/sowExecCoaching';
import type { SowCoachingPhase, SowExecCoachingFields, SowExecCoachingScope } from '../../types';

interface Props {
  fields: SowExecCoachingFields;
  scopeDefaults: Record<SowExecCoachingScope, SowExecCoachingFields>;
  tierPresets?: Record<string, SowCoachingPhase[]>;
  onChange: (next: SowExecCoachingFields) => void;
}

const SCOPE_OPTIONS: Array<{ value: SowExecCoachingScope; label: string }> = [
  { value: 'exec_coaching', label: 'Executive Coaching (tiered)' },
  { value: 'c_suite', label: 'C-Suite Coaching' },
  { value: 'executive_partner', label: 'Executive Partner' },
  { value: 'hybrid', label: 'Hybrid (EP + Coach)' },
];

const area: CSSProperties = { width: '100%', height: 'auto', minHeight: 84, padding: '8px 10px', resize: 'vertical', lineHeight: 1.5 };

export function ExecCoachingSowForm({ fields, scopeDefaults, tierPresets, onChange }: Props): JSX.Element {
  const set = (patch: Partial<SowExecCoachingFields>) => onChange({ ...fields, ...patch });
  const isEP = fields.scope === 'executive_partner' || fields.scope === 'hybrid';
  const selectedTier = selectedExecCoachingTier(fields.tierLabel);

  function onScopeChange(scope: SowExecCoachingScope) {
    onChange(applyExecCoachingScope(fields, scopeDefaults[scope]));
  }

  function onTierSelect(value: string) {
    onChange(applyExecCoachingTier(fields, value === 'Other' ? '' : value, tierPresets));
  }

  function setIdentity(key: 'clientCompany' | 'leaderName' | 'coachName' | 'epName', value: string) {
    onChange(updateExecCoachingIdentity(fields, key, value));
  }

  function setLine(idx: number, patch: Partial<{ label: string; amountText: string }>) {
    set({ programLines: fields.programLines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) });
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card">
        <div className="card-hd"><span className="d-h3">Contract basics</span></div>
        <div className="card-bd" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label className="input-label">Scope</label>
            <SowSelect name="scope" className="select" value={fields.scope} onChange={(e) => onScopeChange(e.target.value as SowExecCoachingScope)}>
              {SCOPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </SowSelect>
          </div>
          {fields.scope === 'exec_coaching' && (
            <div>
              <label className="input-label">Tier</label>
              <SowSelect className="select" value={selectedTier} onChange={(e) => onTierSelect(e.target.value)}>
                {EXEC_COACHING_TIER_OPTIONS.map((tier) => <option key={tier} value={tier}>{tier}</option>)}
                <option value="Other">Other…</option>
              </SowSelect>
              {selectedTier === 'Other' && (
                <SowInput name="tierLabel"
                  className="input"
                  style={{ marginTop: 6 }}
                  placeholder='Custom tier label, e.g. "Tier 2"'
                  value={fields.tierLabel}
                  onChange={(e) => onChange(applyExecCoachingTier(fields, e.target.value, tierPresets))}
                />
              )}
              {selectedTier !== 'Other' && (
                <span className="d-meta" style={{ fontSize: 11.5, color: 'var(--mute)' }}>Loads the standard {selectedTier} phases below.</span>
              )}
            </div>
          )}
          <div>
            <label className="input-label">Client company</label>
            <SowInput name="clientCompany" className="input" value={fields.clientCompany} onChange={(e) => setIdentity('clientCompany', e.target.value)} />
          </div>
          <div>
            <label className="input-label">Opportunity # (O#)</label>
            <SowInput name="oNumber" className="input d-mono" placeholder="O13693" value={fields.oNumber} onChange={(e) => set({ oNumber: e.target.value })} />
          </div>
          <div>
            <label className="input-label">Contract date</label>
            <SowInput name="contractDate" className="input" type="date" value={fields.contractDate} onChange={(e) => set({ contractDate: e.target.value })} />
          </div>
          <div>
            <label className="input-label">Leader (coachee)</label>
            <SowInput name="leaderName" className="input" value={fields.leaderName} onChange={(e) => setIdentity('leaderName', e.target.value)} />
          </div>
          <div>
            <label className="input-label">Coach</label>
            <SowInput name="coachName" className="input" value={fields.coachName} onChange={(e) => setIdentity('coachName', e.target.value)} />
          </div>
          {isEP && (
            <div>
              <label className="input-label">Executive Partner</label>
              <SowInput name="epName" className="input" value={fields.epName} onChange={(e) => setIdentity('epName', e.target.value)} />
            </div>
          )}
          <div>
            <label className="input-label">Service name (title)</label>
            <SowInput name="serviceName" className="input" value={fields.serviceName} onChange={(e) => set({ serviceName: e.target.value })} />
          </div>
          <div>
            <label className="input-label">Services heading</label>
            <SowInput name="servicesHeading" className="input" value={fields.servicesHeading} onChange={(e) => set({ servicesHeading: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-hd"><span className="d-h3">Services / parties</span></div>
        <div className="card-bd">
          <SowTextarea name="servicesIntro" className="input" rows={4} style={area} value={fields.servicesIntro} onChange={(e) => set({ servicesIntro: e.target.value })} />
        </div>
      </div>

      <div className="card">
        <div className="card-hd"><span className="d-h3">Desired outcomes</span></div>
        <div className="card-bd">
          <SowTextarea name="desiredOutcomes" className="input" rows={4} style={area} value={fields.desiredOutcomes} onChange={(e) => set({ desiredOutcomes: e.target.value })} />
        </div>
      </div>

      <div className="card">
        <div className="card-hd">
          <span className="d-h3">Approach to the project</span>
          <label style={{ fontSize: 12.5, color: 'var(--mute)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <SowInput name="showPhaseHours" type="checkbox" checked={fields.showPhaseHours} onChange={(e) => set({ showPhaseHours: e.target.checked })} />
            Show per-phase hours
          </label>
        </div>
        <div className="card-bd">
          <PhaseHoursEditor phases={fields.phases} contractedHours={0} onChange={(phases) => set({ phases })} />
        </div>
      </div>

      <div className="card">
        <div className="card-hd">
          <span className="d-h3">Budget</span>
          <Button variant="ghost" size="sm" onClick={() => set({ programLines: [...fields.programLines, { label: '', amountText: '' }] })}>+ line</Button>
        </div>
        <div className="card-bd" style={{ display: 'grid', gap: 10 }}>
          {fields.programLines.map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 8, alignItems: 'end' }}>
              <div>
                <label className="input-label">Program line label</label>
                <SowInput className="input" name={`programLines.${i}.label`} value={l.label} onChange={(e) => setLine(i, { label: e.target.value })} />
              </div>
              <div>
                <label className="input-label">Amount</label>
                <SowInput className="input d-tnum" placeholder="$XX,XXX" name={`programLines.${i}.amountText`} value={l.amountText} onChange={(e) => setLine(i, { amountText: e.target.value })} />
              </div>
              <Button variant="ghost" size="sm" disabled={fields.programLines.length <= 1} onClick={() => set({ programLines: fields.programLines.filter((_, j) => j !== i) })}>Remove</Button>
            </div>
          ))}
          <div>
            <label className="input-label">Additional fees</label>
            <SowTextarea name="additionalFeesText" className="input" rows={3} style={area} value={fields.additionalFeesText} onChange={(e) => set({ additionalFeesText: e.target.value })} />
          </div>
          <div>
            <label className="input-label">Timing</label>
            <SowTextarea name="budgetTimingText" className="input" rows={3} style={area} value={fields.budgetTimingText} onChange={(e) => set({ budgetTimingText: e.target.value })} />
          </div>
          <div>
            <label className="input-label">Extension terms</label>
            <SowTextarea name="extensionText" className="input" rows={2} style={area} value={fields.extensionText} onChange={(e) => set({ extensionText: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-hd"><span className="d-h3">Project terms</span></div>
        <div className="card-bd">
          <SowTextarea name="projectTermsText" className="input" rows={5} style={area} value={fields.projectTermsText} onChange={(e) => set({ projectTermsText: e.target.value })} />
        </div>
      </div>

      <div className="card">
        <div className="card-hd"><span className="d-h3">Account manager</span></div>
        <div className="card-bd">
          <SowInput name="accountManagerLine" className="input" style={{ width: '100%' }} aria-label="Account manager details" placeholder="Name | Title | email | phone" value={fields.accountManagerLine} onChange={(e) => set({ accountManagerLine: e.target.value })} />
        </div>
      </div>
    </div>
  );
}
